<template>
  <v-card class="rounded-card">
    <v-card-text>
      <p class="headline mb-1 text--primary font-weight-bold">
        {{ resposta.fornecedor.pessoa.nome }}
        <px-popover-fornecedor :fornecedor="resposta.fornecedor" show-score/>
        <px-favoritar-resposta v-if="hasModuleGerencia"
                               :resposta="resposta"
                               :cotacao="resposta.cotacao"
                               @update="onUpdate" />
      </p>
      <p class="subtitle-card-detail">
        <v-icon>mdi-domain</v-icon>
        {{ resposta.fornecedor.pessoa.cnpj | cnpj }}
      </p>
      <p class="subtitle-card-detail" v-if="resposta.fornecedor.codigoInterno">
        <v-icon>mdi-alpha-c-box-outline</v-icon>
        <px-span-tooltip
          bottom
          :text="resposta.fornecedor.codigoInterno"
          tooltip="Código interno do fornecedor"/>
      </p>
      <div class="text--primary">
        <v-row dense>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Forma pagamento</span>
            <div>
              <px-forma-pagamento v-if="resposta.formaPagamento"
                                  :forma-pagamento="resposta.formaPagamento"
                                  :prazo-pagamento="resposta.prazoPagamento"/>
              <span v-if="!resposta.formaPagamento">Não informado</span>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Valido até</span>
            <div>
              <span v-if="resposta.validade">{{ resposta.validade | date }}</span>
              <span v-else>Não informado</span>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Produtos</span>
            <div>
              {{ resposta.quantidadeProdutos }}/{{ resposta.cotacao.quantidadeProdutos }} produto(s)
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Valor de frete</span>
            <div>
              {{ resposta.frete.totalTransporte | currency }}
            </div>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Tipo de frete</span>
            <div>
              {{ resposta.frete.tipoFrete | naoInformado }}
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Data de entrega</span>
            <div>
              <span v-if="resposta.cotacao.previsaoEntrega">
                {{ resposta.cotacao.previsaoEntrega | date}}
              </span>
              <span v-else>Não informado</span>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Projeto</span>
            <div>
              {{ projeto | naoInformado }}
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Centro de custo</span>
            <div>
              {{ centroCusto | naoInformado }}
            </div>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Observação do frete</span>
            <div>
              <px-show-large-text :large-text="resposta.frete.observacaoFrete"/>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Observação geral</span>
            <div>
              <px-show-large-text :large-text="resposta.observacao"/>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Finalidade da Compra</span>
            <div v-if="resposta.cotacao.finalidadeCompra">
              <px-finalidade-compra-value :finalidade-compra="resposta.cotacao.finalidadeCompra"/>
            </div>
            <div v-else>
              Não Informado
            </div>
          </v-col>
           <v-col cols="12" sm="3" v-if="resposta.frete.ofertaFreteGratis">
            <span class="caption font-weight-bold">Frete grátis a partir de</span>
            <div>
              {{ resposta.frete.valorMinimoFreteGratisConvertido | currency }}
              <span>
                ({{ resposta.frete.valorMinimoFreteGratis |
                currency(simboloMoeda, 2, configuracaoFormatacaoMoeda) }})
              </span>
            </div>
          </v-col>
        </v-row>
        <v-row class="mt-3" dense>
          <v-col cols="12" sm="6">
            <span class="caption font-weight-bold">Local de entrega</span>
            <div class="mt-2">
              <px-detalhes-comprador dense
                                     :comprador="resposta.cotacao.compradorEntrega"/>
            </div>
          </v-col>
          <v-col cols="12" sm="6">
            <span class="caption font-weight-bold">Local de faturamento</span>
            <div class="mt-2">
              <px-detalhes-comprador dense
                                     :comprador="resposta.cotacao.comprador"/>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { TipoFrete } from 'px-business-components';
import ObjectUtils from '@/utils/object-utils';
import moedaMixin from '@/mixins/moeda-mixin';
import PxFavoritarResposta from '../PxFavoritarResposta.vue';

export default {
  mixins: [moedaMixin],
  props: {
    resposta: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('user', ['hasModuleGerencia']),
    moeda() {
      return this.resposta.moeda;
    },
    isCif() {
      return this.resposta.frete.tipoFrete === TipoFrete.keys.CIF.key;
    },
    labelFrete() {
      return this.isCif ? 'Prazo de entrega' : 'Prazo de embarque';
    },
    centroCusto() {
      return this.resposta.cotacao.centroCusto
        ? ObjectUtils.descWithCodigoInterno(this.resposta.cotacao.centroCusto) : null;
    },
    projeto() {
      return this.resposta.cotacao.projeto
        ? ObjectUtils.descWithCodigoInterno(this.resposta.cotacao.projeto) : null;
    },
  },
  methods: {
    onUpdate() {
      this.$emit('update');
    },
  },
  components: {
    PxFavoritarResposta,
  },
};
</script>
